import React, { useEffect, useState, createRef, useRef } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Button,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';

import {
  assignTasksTo,
  unassignTasks,
  updateVisibleTxnOpps,
  orderInspection,
  updateBidInPrism,
  syncPurchaseState,
  runStatusOverrideSync,
  refreshTxnInspection,
  terminateTxnOpps,
  unterminateTxnOpps,
  exportAllocationLogs,
  getDescriptiveFitDetails,
  reloadTxnOpps,
  deleteTxnOpps,
  setConfirmationModalState,
  setChannelWorkflowLoadingOverlayState,
  setIsFetchingClientTxnOppsData,
  fetchQueueTxnOppsData,
  createExploreSystemView,
  appNotify,
  fetchOpportunityHistory,
} from 'store/actions';

import { push } from 'connected-react-router';

import { Stack, LoadingOverlayWrapper } from 'components/layout';
import AssignModal from 'components/underwrite/shared/workflow/AssignModal';
import TerminateModal from './TerminateModal';
import { DeleteModal } from 'components/underwrite/shared/workflow';
import ChannelWorkflowGrid from './ChannelWorkflowGrid';
import ProspectInfoModal from './ProspectInfoModal';
import ViewDescriptiveFitDetailsModal from './ViewDescriptiveFitDetailsModal';
import LayoutPopover from './LayoutPopover';
import { Constants } from 'utilities/constants';
import { sendClientLog } from 'services/logService';
import ViewCrmModal from 'components/underwrite/acquisition/propertyUnderwrite/header/ViewCrmModal';
import ViewOpportunityHistoryModal from 'components/underwrite/acquisition/propertyUnderwrite/header/ViewOpportunityHistoryModal';
import AutoPricingResultsModal from './AutoPricingResultsModal';
import { format as dateFormat } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';

const gridRef = createRef();

const ChannelWorkflow = ({
  assignTasksTo,
  classes,
  workflowGridNeedsToRefresh,
  deleteTxnOpps,
  isFetchingClientTxnOppsData,
  channelWorkflowLoadingOverlayState,
  match,
  orderInspection,
  refreshTxnInspection,
  reloadTxnOpps,
  setConfirmationModalState,
  setIsFetchingClientTxnOppsData,
  setChannelWorkflowLoadingOverlayState,
  syncPurchaseState,
  runStatusOverrideSync,
  terminateTxnOpps,
  unassignTasks,
  unterminateTxnOpps,
  exportAllocationLogs,
  getDescriptiveFitDetails,
  updateBidInPrism,
  updateVisibleTxnOpps,
  fetchQueueTxnOppsData,
  user,
  push,
  createExploreSystemView,
  appNotify,
  fetchOpportunityHistory,
}) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isProspectInfoModalOpen, setIsProspectInfoModalOpen] = useState(false);
  const [isCrmInfoModalOpen, setIsCrmInfoModalOpen] = useState(false);
  const [isAutoPricingResultsModalOpen, setIsAutoPricingResultsModalOpen] = useState(false);
  const [gridKey, setGridKey] = useState(0);

  const isVisited = useRef(false);
  useEffect(() => {
    if (match) isVisited.current = true;
  }, [match]); 

  function handleDataRefreshPressed(){
    setGridKey(gridKey+1);
    sendClientLog('Info', 'Channel Workflow - User started clientTxnOpps data fetch - channel workflow refresh button');
    fetchQueueTxnOppsData(Constants.Module.UNDERWRITE)
  }

  /**Open Crm Info Modal */
  function handleOpenCrmInfoModal(selectedRows) {
    setSelectedRows(selectedRows);
    setIsCrmInfoModalOpen(true);
  }

  const handleOpenOpportunityHistoryModal = (selectedRows) => {
    setSelectedRows(selectedRows);
    if (selectedRows.length === 1) {
      fetchOpportunityHistory({ txnOppId: selectedRows[0].id });
    }
  }

  // Push to PRISM
  function openPushToCrmModal(selectedRows) {
    setSelectedRows(selectedRows);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handlePushToCrm(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to push ${selectedRows.length} transaction ${selectedRows.length > 1 ? 'opportunities' : 'opportunity'} to CRM?`
    });
  }

  function handlePushToCrm(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    updateBidInPrism(selectedRows.map(opp => opp.id));
  }

  // Sync CRM state
  function handleSyncPurchaseState(txnOppIds) {
    syncPurchaseState(txnOppIds);
  }

  function handleStatusOverrideSync(txnOppIds){
    runStatusOverrideSync(txnOppIds);
  }

  /**Inspection */
  function openInspectionModal(selectedRows) {
    setSelectedRows(selectedRows);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleOrderInspection(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to order drive by inspection for ${selectedRows.length} transaction opportunities?`
    });
  }

  function handleOrderInspection(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    orderInspection(selectedRows.map(opp => opp.id));
  }

  function handleRefreshInspection(selectedRows) {
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleRefreshInspectionConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to refresh inspection for ${selectedRows.length} transaction opportunities?`
    });
  }

  function handleRefreshInspectionConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    refreshTxnInspection(selectedRows.map(txnOpp => txnOpp.id));
  }

  /**Include */
  function handleShowOpp(selectedRows) {
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: () => handleShowOppConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to show ${selectedRows.length} transaction opportunities?`
    });
  }

  function handleShowOppConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    // const rowsToInclude = selectedRows.filter(opp => opp.status !== 'Available'); // Find rows that are NOT currently included, call api with those IDs
    const rowsToInclude = selectedRows;
    if (rowsToInclude.length) {
      const txnOppIds = rowsToInclude.map(({ id }) => id);
      updateVisibleTxnOpps(txnOppIds, Constants.Visibility.SHOW);
    }
  }

  /**Exclude */
  function handleHideOpp(selectedRows) {
    //const filteredRows = selectedRows.filter(opp => opp.included); // Get selected rows, find those that ARE currently included, call api with those IDs
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleHideOppConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to hide ${selectedRows.length} transaction opportunities?`
    });
  }

  function handleHideOppConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    if (selectedRows.length) {
      const txnOppIds = selectedRows.map(({ id }) => id);
      updateVisibleTxnOpps(txnOppIds, Constants.Visibility.HIDE);
    }
  }

  /**Reload TxnOpp Data */
  function handleReloadTxnOpps(selectedRows) {
    setSelectedRows(selectedRows);
    const postAcqCount = selectedRows.filter(opp => opp.opportunityStatus === Constants.OpportunityStatus.COMPLETED && opp.isPostAcquisitionUnderwrite === false).length; // Number of opps that will flip from acq to post-acq
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleReloadTxnOppsConfirmation(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to reload ${selectedRows.length} transaction ${selectedRows.length > 1 ? 'opportunities' : 'opportunity'}? This will archive existing data and pull latest information.`,
      warningText: postAcqCount > 0 ? `${postAcqCount === 1 && selectedRows.length === 1 ? 'This' : postAcqCount} ${postAcqCount > 1 ? 'opportunities' : 'opportunity'} will be moved into post-acquisitions mode.` : ''
    });
  }

  function handleReloadTxnOppsConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    reloadTxnOpps(selectedRows.map(row => row.id));
  }

  /**Export Allocation Logs */
  function handleExportAllocationLogs(selectedRows) {
    const idsToExport = selectedRows.map(opp => opp.id);
    exportAllocationLogs(idsToExport);
  }

  /**Export Allocation Logs */
  function handleGetDescriptiveFitDetails(selectedRows) {
    const idsToExport = selectedRows.map(opp => opp.id);
    getDescriptiveFitDetails(idsToExport);
  }

  /**Assign opp */
  function handleOpenAssign(selectedRows) {
    setSelectedRows(selectedRows);
    setIsAssignModalOpen(true);
  }

  function assignTasks(type, selectedRows, selectedUserId) {
    const idsToAssign = type === 'auction' ? [selectedRows[0].portfolioId] : selectedRows.map(opp => opp.id);
    assignTasksTo(type, idsToAssign, selectedUserId, user.id); // Assign all tasks, whether coming from Imported, Available, or currently assigned to a user
    if (isAssignModalOpen) setIsAssignModalOpen(false);
  }

  /**Unassign opp */
  function handleUnassign(type, selectedRows) {
    if (type === 'txnOpp') {
      unassignTasks(type, selectedRows.map(opp => opp.id), user.id);
    } else if (type === 'auction') {
      unassignTasks(type, [selectedRows[0].portfolioId], user.id);
    }
  }

  /**Assign auction */
  function handleAssignAuction(selectedRows) {
    const belongsToOther = selectedRows[0].auctionOwnerUserId != null && selectedRows[0].auctionOwnerUserId !== user.id;
    if (belongsToOther) {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => handleAssignAuctionConfirmation(selectedRows),
        handleCancellation: closeConfirmationModal,
        titleText: 'Confirm',
        contentText: `This auction (${selectedRows[0].auctionName}) is currently assigned to ${selectedRows[0].auctionOwnerUsername}. Are you sure you want to assign it to yourself?`
      });
    } else {
      assignTasks('auction', selectedRows, user.id);
    }
  }

  function handleAssignAuctionConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    assignTasks('auction', selectedRows, user.id);
  }

  /**Unassign auction */
  function handleUnassignAuction(selectedRows) {
    const belongsToOther = selectedRows[0].auctionOwnerUserId != null && selectedRows[0].auctionOwnerUserId !== user.id;
    if (belongsToOther) {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => handleUnassignAuctionConfirmation(selectedRows),
        handleCancellation: closeConfirmationModal,
        titleText: 'Confirm',
        contentText: `This auction (${selectedRows[0].auctionName}) is currently assigned to ${selectedRows[0].auctionOwnerUsername}. Are you sure you want to unassign it?`
      });
    } else {
      handleUnassign('auction', selectedRows);
    }
  }

  function handleUnassignAuctionConfirmation(selectedRows) {
    closeConfirmationModal();   // close confirm modal first
    handleUnassign('auction', selectedRows);
  }

  /**Open Prospect Info Modal */
  function handleOpenProspectInfoModal(selectedRows) {
    console.log(selectedRows);
    setSelectedRows(selectedRows);
    setIsProspectInfoModalOpen(true);
  }

  /**Terminate */
  function handleOpenTerminate(selectedRows) {
    setSelectedRows(selectedRows);
    setIsTerminateModalOpen(true);
  }

  function terminateTasks(selectedReason) {
    const idsToTerminate = selectedRows.map(opp => opp.id);
    terminateTxnOpps(idsToTerminate, selectedReason);
    if (isTerminateModalOpen) setIsTerminateModalOpen(false);
  }

  /**Unterminate */
  function handleOpenUnterminate(selectedRows) {
    setSelectedRows(selectedRows);
    //setIsUnterminateModalOpen(true);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleUnterminate(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to unterminate ${selectedRows.length} transaction ${selectedRows.length > 1 ? 'opportunities' : 'opportunity'}?`
    });
  }

  function handleUnterminate(selectedRows) {
    closeConfirmationModal();
    unterminateTasks(selectedRows);
  }

  function unterminateTasks(selectedRows) {
    const idsToUnterminate = selectedRows.map(opp => opp.id);
    unterminateTxnOpps(idsToUnterminate);
  }

  /**Delete TxnOpps */
  function handleDeleteTxnOpps(selectedRows) {
    setSelectedRows(selectedRows);
    setIsDeleteModalOpen(true);
  }

  /**View on Explore */
  function handleViewOnExplore(selectedRows, byTxnOppId = false) {
    const workflowField = byTxnOppId ? 'id' : 'asgPropID';
    const exploreField = byTxnOppId ? 'transactionOpportunityId' : 'asgPropID';
    const filterIds = [];
    selectedRows.forEach(row => {
      if (row[workflowField] && !filterIds.includes(row[workflowField])) {
        filterIds.push(row[workflowField]);
      }
    });
    // construct filterValue
    const filterValue = [exploreField, 'in', filterIds];
    // construct filterExpression to display on UI
    const filterExpression = [];
    filterIds.forEach(id => {
      filterExpression.push([exploreField, '=', id]);
      filterExpression.push('or');
    });
    filterExpression.pop();
    const newFilter = { filterValue, filterExpression };
    createExploreSystemView(`Workflow opportunities`, newFilter);
    push(Constants.Routes.EXPLORE);
  }

  function deleteTasks() {
    const idsToDelete = selectedRows.map(opp => opp.id);
    deleteTxnOpps(idsToDelete);
    if (isDeleteModalOpen) setIsDeleteModalOpen(false);
  }

  /**Shared method to close confirmation modal */
  function closeConfirmationModal() {
    setConfirmationModalState({ isModalOpen: false });
  }

  /**Open AutoPricingResults Modal**/
  function handleOpenAutoPricingResultsModal(selectedRows) {
    setSelectedRows(selectedRows);
    setIsAutoPricingResultsModalOpen(true);
    closeConfirmationModal();
  }

  function handleOpenAutoPricingModal(selectedRows) {
    setSelectedRows(selectedRows);
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => handleOpenAutoPricingResultsModal(selectedRows),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to run autopricing on ${selectedRows.length} transaction ${selectedRows.length > 1 ? 'opportunities' : 'opportunity'}?`
    });
  }
  //#endregion

  function handleExcelExport() {
    setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Exporting...' });

    const maxExportCount = 20000;
    const visibleRowsCount = gridRef.current.instance.totalCount()
    if(visibleRowsCount > maxExportCount) {
      appNotify(`Export is limited to ${maxExportCount.toLocaleString()} rows.`, 'error');
    } else {
      const timestamp = dateFormat(new Date(), '_yyyy-MM-dd_HHmmss');
      const fileName = 'ReVUe_WorkflowExport' + timestamp;
      const sheetName = 'Main Sheet';
      const customizeCell = options => {
        const { gridCell, excelCell } = options;
        const { column } = gridCell;
        // wrap text in column headers
        if (gridCell.rowType === "header") {
          excelCell.alignment = {
            horizontal: "center",
            vertical: "top",
            wrapText: true
          };
        }
        // display emoji for boolean values
        if (column.dataType === "boolean" && gridCell.rowType === "data") {
          excelCell.value === true
          ? excelCell.value = column.trueText
          : excelCell.value = column.falseText;
        }
      }
      dataGridExtension.exportToExcel(gridRef, fileName, sheetName, customizeCell);
    }

    setChannelWorkflowLoadingOverlayState(false, null);
    sendClientLog('Info', 'Channel Workflow - User clicked all channel workflow column Excel export');
  }

  return ((match || isVisited.current) &&
    <Stack className={classes.root} style={!match // Channel Workflow is always mounted. Use CSS to hide when viewing other routes
      ? { position: 'absolute', left: '-100vw', visibility: 'hidden' }
      : null
    }>
      <div className={classes.headerContainer}>
        <Typography variant="h5">Workflow</Typography>
        <LayoutPopover
          gridRef={gridRef}
        />
        <div style={{ flexGrow: 1 }} />
        <Tooltip title='Refresh Grid Data'>
          <Badge
            invisible={isFetchingClientTxnOppsData || !workflowGridNeedsToRefresh}
            classes={{ colorPrimary: classes.colorPrimary, badge: classes.refreshBadge }}
            color='primary'
            variant="dot"
            overlap='rectangular'
          >
            <IconButton disabled={isFetchingClientTxnOppsData} onClick={handleDataRefreshPressed}>
              <RefreshIcon />
            </IconButton>
          </Badge>
        </Tooltip>
        <Tooltip title="Export visible data to Excel">
          <Button
            variant='contained'
            color='primary'
            onClick={handleExcelExport}
            style={{ marginLeft: 8 }}
          >
            Export
          </Button>
        </Tooltip>
      </div>
      <LoadingOverlayWrapper
        active={isFetchingClientTxnOppsData || (channelWorkflowLoadingOverlayState && channelWorkflowLoadingOverlayState.isLoading)}
        message={isFetchingClientTxnOppsData ? 'Fetching task data...' : channelWorkflowLoadingOverlayState.message}
      >
        <Paper className={classes.channelWorkflowPaper}>
          <ChannelWorkflowGrid
            ref={gridRef}
            updateGrid={gridKey}
            openInspectionModal={openInspectionModal}
            openPushToCrmModal={openPushToCrmModal}
            handleSyncPurchaseState={handleSyncPurchaseState}
            handleStatusOverrideSync={handleStatusOverrideSync}
            handleShowOpp={handleShowOpp}
            handleUnassign={handleUnassign}
            handleHideOpp={handleHideOpp}
            handleOpenAssign={handleOpenAssign}
            handleRefreshInspection={handleRefreshInspection}
            assignTasks={assignTasks}
            handleAssignAuction={handleAssignAuction}
            handleUnassignAuction={handleUnassignAuction}
            handleOpenTerminate={handleOpenTerminate}
            handleOpenUnterminate={handleOpenUnterminate}
            handleDeleteTxnOpps={handleDeleteTxnOpps}
            handleReloadTxnOpps={handleReloadTxnOpps}
            handleViewOnExplore={handleViewOnExplore}
            handleExportAllocationLogs={handleExportAllocationLogs}
            handleGetDescriptiveFitDetails={handleGetDescriptiveFitDetails}
            handleOpenProspectInfoModal={handleOpenProspectInfoModal}
            handleOpenCrmInfoModal={handleOpenCrmInfoModal}
            handleOpenOpportunityHistoryModal={handleOpenOpportunityHistoryModal}
            handleOpenAutoPricingModal={handleOpenAutoPricingModal}
            setGridLoading={setIsFetchingClientTxnOppsData}
          />
          <ViewCrmModal
            open={isCrmInfoModalOpen}
            handleClose={() => setIsCrmInfoModalOpen(false)}
            txnOppId={selectedRows.length === 1 && selectedRows[0].id}
          />
          <ViewOpportunityHistoryModal
            txnOppId={selectedRows.length === 1 && selectedRows[0].id}
          />
          <ViewDescriptiveFitDetailsModal />
          <AutoPricingResultsModal
            isOpen={isAutoPricingResultsModalOpen}
            selectedRows={selectedRows}
            setIsOpen={setIsAutoPricingResultsModalOpen}
          />
          <ProspectInfoModal
            selectedRows={selectedRows}
            isOpen={isProspectInfoModalOpen}
            setIsOpen={setIsProspectInfoModalOpen}
          />
          <AssignModal
            module={Constants.Module.UNDERWRITE}
            selectedRows={selectedRows}
            isOpen={isAssignModalOpen}
            setIsOpen={setIsAssignModalOpen}
            assignTasks={assignTasks}
          />
          <TerminateModal
            txnOpps={selectedRows}
            isOpen={isTerminateModalOpen}
            setIsOpen={setIsTerminateModalOpen}
            terminateTasks={terminateTasks}
          />
          <DeleteModal
            module={Constants.Module.UNDERWRITE}
            selectedRows={selectedRows}
            isOpen={isDeleteModalOpen}
            setIsOpen={setIsDeleteModalOpen}
            deleteItems={deleteTasks}
          />
        </Paper>
      </LoadingOverlayWrapper>
    </Stack>
  );
};

const styles = theme => ({
  root: {
    width: '100%',
    height: '100%',
    padding: '0px 48px'
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '8px 0'
  },
  refreshBadge: {
    marginTop: 8,
    marginRight: 8
  },
  colorPrimary: {
    backgroundColor: 'Red', //'#8c8c8c', '#999999'
    color: 'White',
  },
  toggle: {
    marginRight: 0
  },
  channelWorkflowPaper: {
    flexGrow: 1
  }
});

const mapState = state => {
  return {
    user: state.appState.user,
    workflowGridNeedsToRefresh: state.clientTxnOppsState.workflowGridNeedsToRefresh,
    channelWorkflowLoadingOverlayState: state.channelWorkflowState.channelWorkflowLoadingOverlayState
  }
};
const mapDispatch = {
  assignTasksTo,
  unassignTasks,
  terminateTxnOpps,
  unterminateTxnOpps,
  exportAllocationLogs,
  getDescriptiveFitDetails,
  reloadTxnOpps,
  deleteTxnOpps,
  updateVisibleTxnOpps,
  orderInspection,
  syncPurchaseState,
  runStatusOverrideSync,
  updateBidInPrism,
  refreshTxnInspection,
  setConfirmationModalState,
  setChannelWorkflowLoadingOverlayState,
  createExploreSystemView,
  push,
  appNotify,
  setIsFetchingClientTxnOppsData,
  fetchQueueTxnOppsData,
  fetchOpportunityHistory
};

export default compose(
  withStyles(styles),
  connect(
    mapState,
    mapDispatch
  )
)(ChannelWorkflow);
